import { Client } from '@stomp/stompjs'

// Runtime config injected by /config.js (Docker/Dokploy), fallback to build-time env
const runtimeConfig = (typeof window !== 'undefined' && window.APP_CONFIG) || {}
const API_BASE_URL = runtimeConfig.VITE_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || ''

// Derive wss:// URL from the http base so sockjs-client is not needed
const httpBase = API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '')
const WS_URL = httpBase.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:') + '/api/ws/terminal'

const getAuthToken = () => localStorage.getItem('auth_token')

/**
 * Append the JWT as a query parameter. The WebSocket and EventSource APIs
 * cannot set an Authorization header, so the backend's JwtAuthFilter also
 * reads the token from the query string. Resolved per call, not once at module
 * load, so a token obtained after this module is imported is still picked up.
 */
const withToken = (url) => {
  const token = getAuthToken()
  if (!token) return url
  return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
}

class WebSocketService {
  constructor() {
    this.client = null
    this.subscriptions = new Map()
    this.connected = false
    this.connectionCallbacks = []
    this.messageHandlers = new Map()
  }

  connect() {
    return new Promise((resolve, reject) => {
      if (this.connected && this.client?.active) {
        resolve()
        return
      }

      this.client = new Client({
        brokerURL: withToken(WS_URL),
        reconnectDelay: 5000,
        heartbeatIncoming: 4000,
        heartbeatOutgoing: 4000,
        debug: (str) => {
          // Uncomment for debugging: console.log('STOMP:', str)
        },
        onConnect: () => {
          console.log('✓ WebSocket connected')
          this.connected = true
          this.connectionCallbacks.forEach(cb => cb(true))
          resolve()
        },
        onDisconnect: () => {
          console.log('✗ WebSocket disconnected')
          this.connected = false
          this.connectionCallbacks.forEach(cb => cb(false))
        },
        onStompError: (frame) => {
          console.error('WebSocket STOMP error:', frame)
          this.connected = false
          this.connectionCallbacks.forEach(cb => cb(false))
        },
        onWebSocketError: (event) => {
          console.error('WebSocket error:', event)
          this.connected = false
        }
      })

      this.client.activate()
    })
  }

  disconnect() {
    if (this.client) {
      this.client.deactivate()
      this.subscriptions.clear()
      this.connected = false
    }
  }

  subscribe(destination, callback) {
    if (!this.client?.active) {
      console.error('WebSocket not connected')
      return null
    }

    const subscription = this.client.subscribe(destination, (message) => {
      try {
        const body = JSON.parse(message.body)
        callback(body)
      } catch (e) {
        callback({ type: 'raw', content: message.body })
      }
    })

    this.subscriptions.set(destination, subscription)
    return subscription
  }

  unsubscribe(destination) {
    const subscription = this.subscriptions.get(destination)
    if (subscription) {
      subscription.unsubscribe()
      this.subscriptions.delete(destination)
    }
  }

  send(destination, body) {
    if (!this.client?.active) {
      console.error('WebSocket not connected')
      return false
    }

    this.client.publish({
      destination,
      body: JSON.stringify(body)
    })
    return true
  }

  executeCommand(serverId, command, timeoutMs = 60000) {
    return this.send('/app/terminal/execute', {
      serverId,
      command,
      timeoutMs
    })
  }

  executeLocalCommand(command, timeoutMs = 60000) {
    return this.send('/app/terminal/execute-local', {
      command,
      timeoutMs
    })
  }

  cancelCommand() {
    return this.send('/app/terminal/cancel', {})
  }

  onConnectionChange(callback) {
    this.connectionCallbacks.push(callback)
    return () => {
      this.connectionCallbacks = this.connectionCallbacks.filter(cb => cb !== callback)
    }
  }
}

export const wsService = new WebSocketService()

// SSE (Server-Sent Events) for email creation streaming
export function createEmailCreationStream(jobId, serverId, onMessage, onError, onComplete) {
  const url = `${API_BASE_URL}/api/email-creation/stream/${jobId}?serverId=${serverId}`

  const eventSource = new EventSource(withToken(url))

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data)
      onMessage?.(data)
    } catch (e) {
      onMessage?.({ type: 'raw', data: event.data })
    }

    // Check for completion events
    if (event.data.includes('"type":"complete"') || event.data.includes('"type":"EXIT"')) {
      setTimeout(() => {
        eventSource.close()
        onComplete?.()
      }, 100)
    }
  }

  eventSource.onerror = (error) => {
    console.error('SSE error:', error)
    onError?.(error)
    eventSource.close()
  }

  return {
    close: () => eventSource.close()
  }
}

// SSE for single email creation
export function createSingleEmailStream(request, onMessage, onError, onComplete) {
  // Use fetch with ReadableStream for POST request streaming
  return fetch(`${API_BASE_URL}/api/email-creation/create-single`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request)
  }).then(response => {
    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    function read() {
      return reader.read().then(({ done, value }) => {
        if (done) {
          onComplete?.()
          return
        }

        const text = decoder.decode(value, { stream: true })
        // Parse SSE format (data: {...}\n\n)
        const lines = text.split('\n\n')
        lines.forEach(line => {
          if (line.startsWith('data:')) {
            const data = line.substring(5).trim()
            try {
              const parsed = JSON.parse(data)
              onMessage?.(parsed)
            } catch (e) {
              onMessage?.({ type: 'raw', data })
            }
          }
        })

        return read()
      })
    }

    return read()
  }).catch(error => {
    onError?.(error)
  })
}
